import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PrintIcon from '@mui/icons-material/Print';
import { useSnackbar } from 'notistack';
import { getMonthSummary, getEmployeeMonthDetails } from '../services/schedule';
import { getUsers } from '../services/user';
import PrintableSchedule from '../components/PrintableSchedule';
import { addLog as createLog } from '../services/logs';

const Payroll = () => {
  const [summaryData, setSummaryData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printData, setPrintData] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const fetchData = useCallback(async () => {
    try {
      const users = await getUsers();
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const summary = await getMonthSummary(year, month);

      console.log('Users:', users);
      console.log('Summary:', summary);

      const combinedData = users.map(user => {
        const userSummary = summary.user_summaries.find(s => s.user.id === user.uid) || {
          total_shifts: 0,
          total_minutes: 0,
          locations: []
        };

        return {
          ...user,
          total_shifts: userSummary.total_shifts || 0,
          total_minutes: userSummary.total_minutes || 0,
          locations: userSummary.locations || []
        };
      });

      console.log('Combined data:', combinedData);

      setSummaryData(combinedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Błąd podczas pobierania danych', { variant: 'error' });
    }
  }, [selectedMonth, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData, selectedMonth]);

  const handlePreviousMonth = () => {
    setSelectedMonth(prev => {
      const date = new Date(prev);
      date.setMonth(date.getMonth() - 1);
      return date;
    });
  };

  const handleNextMonth = () => {
    setSelectedMonth(prev => {
      const date = new Date(prev);
      date.setMonth(date.getMonth() + 1);
      return date;
    });
  };

  const handleShowDetails = (employee) => {
    setSelectedEmployee(employee);
    setDetailsOpen(true);
  };

  const handlePrint = async (employee) => {
    console.log('Print employee data:', employee);
    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const data = await getEmployeeMonthDetails(employee.uid, year, month, employee);
      setPrintData(data);
      setPrintDialogOpen(true);

      // Dodaj log o wydruku grafiku
      const logDetails = {
        employee_id: employee.uid,
        year: year,
        month: month,
        print_type: 'schedule'
      };

      // Dodaj first_name i last_name tylko jeśli istnieją
      if (employee.first_name) logDetails.first_name = employee.first_name;
      if (employee.last_name) logDetails.last_name = employee.last_name;

      await createLog({
        action: 'schedule_print',
        entity_type: 'schedule',
        entity_id: `${employee.uid}_${year}_${month}`,
        user_id: employee.uid,
        details: logDetails
      });
    } catch (error) {
      console.error('Error fetching print data:', error);
      enqueueSnackbar('Błąd podczas pobierania danych do wydruku', { variant: 'error' });
    }
  };

  const handlePrintConfirm = () => {
    window.print();
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatMonth = (date) => {
    return new Intl.DateTimeFormat('pl-PL', { 
      year: 'numeric', 
      month: 'long' 
    }).format(date);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontFamily: 'Cinzel, serif',
            fontWeight: 700,
            color: 'primary.main'
          }}
        >
          Lista płac
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button 
            onClick={handlePreviousMonth}
            variant="outlined"
            size="small"
          >
            &lt;
          </Button>
          <Typography 
            variant="h6"
            sx={{ 
              fontFamily: 'Cinzel, serif',
              fontWeight: 600,
              minWidth: 200,
              textAlign: 'center'
            }}
          >
            {formatMonth(selectedMonth)}
          </Typography>
          <Button 
            onClick={handleNextMonth}
            variant="outlined"
            size="small"
          >
            &gt;
          </Button>
        </Box>
      </Box>

      <TableContainer 
        component={Paper} 
        sx={{ 
          backgroundColor: 'background.paper',
          borderRadius: 2
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Zleceniobiorca</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Imię</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Nazwisko</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Liczba zmian</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Całkowity czas</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaryData.map((employee) => (
              <TableRow 
                key={employee.uid}
                sx={{ '&:hover': { backgroundColor: 'rgba(212, 175, 55, 0.1)' } }}
              >
                <TableCell sx={{ fontFamily: 'Montserrat' }}>
                  {employee.username}
                </TableCell>
                <TableCell sx={{ fontFamily: 'Montserrat' }}>
                  {employee.first_name}
                </TableCell>
                <TableCell sx={{ fontFamily: 'Montserrat' }}>
                  {employee.last_name}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Montserrat' }}>
                  {employee.total_shifts}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Montserrat' }}>
                  {formatTime(employee.total_minutes)}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleShowDetails(employee)}
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { 
                          color: 'primary.light',
                          backgroundColor: 'rgba(212, 175, 55, 0.1)'
                        }
                      }}
                    >
                      <InfoIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handlePrint(employee)}
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { 
                          color: 'primary.light',
                          backgroundColor: 'rgba(212, 175, 55, 0.1)'
                        }
                      }}
                    >
                      <PrintIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog ze szczegółami */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            backgroundColor: 'background.paper',
            borderRadius: 2
          }
        }}
      >
        <DialogTitle>
          <Typography 
            variant="h5" 
            sx={{ 
              fontFamily: 'Cinzel, serif',
              fontWeight: 600,
              color: 'primary.main'
            }}
          >
            {selectedEmployee?.first_name} {selectedEmployee?.last_name}
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontFamily: 'Montserrat',
              color: 'text.secondary',
              mt: 1 
            }}
          >
            {formatMonth(selectedMonth)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Lokalizacja</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Liczba zmian</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Czas pracy</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEmployee.locations.map((loc) => (
                    <TableRow 
                      key={loc.location_id}
                      sx={{ 
                        '&:hover': { backgroundColor: 'rgba(212, 175, 55, 0.1)' },
                        fontFamily: 'Montserrat'
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'Montserrat' }}>
                        {loc.location_name}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'Montserrat' }}>
                        {loc.shifts_count}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'Montserrat' }}>
                        {formatTime(loc.total_minutes)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow 
                    sx={{ 
                      backgroundColor: 'rgba(212, 175, 55, 0.1)',
                      fontWeight: 600
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>Suma</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {selectedEmployee.total_shifts}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatTime(selectedEmployee.total_minutes)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDetailsOpen(false)}
            variant="outlined"
          >
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog z wydrukiem */}
      <Dialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            backgroundColor: 'background.paper',
            borderRadius: 2
          }
        }}
      >
        <DialogTitle
          sx={{ 
            fontFamily: 'Cinzel, serif',
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          Podgląd wydruku
        </DialogTitle>
        <DialogContent>
          {printData && <PrintableSchedule data={printData} />}
        </DialogContent>
        <DialogActions className="no-print">
          <Button 
            onClick={() => setPrintDialogOpen(false)}
            variant="outlined"
          >
            Anuluj
          </Button>
          <Button 
            onClick={handlePrintConfirm}
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
          >
            Drukuj
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payroll;
